var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// libs
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
// widgets
import Stepper from '@/pages/instamart/campaign/create/widgets/Stepper';
import AdPlacement from '@/pages/instamart/campaign/create/widgets/Steps/AdPlacement';
import AdPreference from '@/pages/instamart/campaign/create/widgets/Steps/AdPreference';
import Products from '@/pages/instamart/campaign/create/widgets/Steps/Products';
import Keywords from '@/pages/instamart/campaign/create/widgets/Steps/Keywords';
import Budget from '@/pages/instamart/campaign/create/widgets/Steps/Budget';
import ReviewCampaign from '@/pages/instamart/campaign/create/widgets/Steps/ReviewCampaign';
// dls
import { Svg16 as ArrowRight } from '@swiggy-private/dls-web-icons/dist/web/generic/chevronRight';
import { Button } from '@swiggy-private/dls-web-core/dist/components/Button';
import { Text } from '@swiggy-private/dls-web-core/dist/components/Text';
// stores
import useWriteCampaignStore from '@/stores/WriteCampaign';
import useSuggestedKeywordsStore from '@/stores/SuggestedKeywords';
import useReadCampaignStore from '@/stores/ReadCampaign';
import useAccountStore from '@/stores/account';
import useConfigStore from '@/stores/Configs';
// helpers
import { getCloudinaryUrl } from '@/helpers/cloudinary';
// includes
import { createCampaignApi } from '@/includes/api/campaign/create';
// constants
import { CampaignWriteModeEnum } from '@/stores/WriteCampaign/constants';
import Routes from '@/constants/Routes';
import { DlsColorTokens, DlsFontVariants } from '@/constants/DLSTokens';
// hooks
import useNudge from '@/hooks/useNudge';
// styles
import styles from './Index.module.scss';
const AdPlacementValidationKeys = ['name'];
const BudgetValidationKeys = ['budget'];
const ProductsValidationKeys = ['candidates'];
const AdPreferenceValidationKeys = [
    'startDate',
    'endDate',
    'cityIds',
    'mealSlots',
    'daysOfWeek',
];
const keywordsValidationKeys = ['keywords'];
const IMAGE = {
    width: 950 * 2,
    src: 'v1689935777/InstamartAssets/B2B%20Assets/Ads%20Platform/Campaign_created.png',
};
const Index = () => {
    var _a;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const storedReadCampaign = useReadCampaignStore((state) => state.campaign);
    const [showFinalStep, setShowFinalStep] = useState(false);
    const [isError, setIsError] = useState(false);
    const [setupInProgress, setSetupInProgress] = useState(true);
    const { setState: setWriteCampaign } = useWriteCampaignStore;
    const clearWriteStore = useWriteCampaignStore((state) => state.clear);
    const isValid = useWriteCampaignStore((state) => state.isValid);
    const campaign = useWriteCampaignStore((state) => ({
        name: state.name,
        startDate: state.startDate,
        endDate: state.endDate,
        budget: state.budget,
        pacingEnabled: state.pacingEnabled,
        type: state.type,
        placementIds: state.placementIds,
        createdBy: state.createdBy,
        updatedBy: state.updatedBy,
        adGroups: state.adGroups,
        biddingStrategy: state.biddingStrategy,
        cityIds: state.cityIds,
        setCityIds: state.setCityIds,
        setAdGroupCityIds: state.setAdGroupCityIds,
    }));
    const cityOptions = useConfigStore((store) => store.cities);
    const { isAnyNudgeVisible } = useNudge();
    const [showWarningModal, setShowWarningModal] = useState(false);
    const loadCampaignDetails = () => {
        if (!(storedReadCampaign === null || storedReadCampaign === void 0 ? void 0 : storedReadCampaign.id)) {
            setIsError(true);
            return;
        }
        const clonedCampaign = structuredClone(storedReadCampaign);
        setWriteCampaign(Object.assign(Object.assign({}, clonedCampaign), { startDate: undefined, endDate: undefined }));
    };
    useEffect(() => {
        // clear existing write campaign data
        // TODO: check placement id reset issue because of this
        setSetupInProgress(true);
        clearWriteStore();
        setSetupInProgress(false);
        if (searchParams.get('mode') === CampaignWriteModeEnum.DUPLICATE) {
            loadCampaignDetails();
        }
        else {
            /**
             * select all cities by default while create campaign
             */
            const allCities = cityOptions.map((cityInfo) => cityInfo.id);
            campaign.setCityIds(allCities);
            campaign.setAdGroupCityIds(allCities);
        }
    }, []);
    const { keywords, customKeywords } = useSuggestedKeywordsStore();
    const getCurrentAccountId = useAccountStore((state) => state.getCurrentAccountId);
    const stepperRef = useRef(null);
    const keywordsValidator = () => {
        const messages = isValid([...keywordsValidationKeys], {
            keywords: [...keywords, ...customKeywords],
        });
        return Object.keys(messages).length === 0;
    };
    const adPlacementValidator = () => {
        const messages = isValid([...AdPlacementValidationKeys]);
        return Object.keys(messages).length === 0;
    };
    const budgetValidator = () => {
        const messages = isValid([...BudgetValidationKeys]);
        return Object.keys(messages).length === 0;
    };
    const productsValidator = () => {
        const messages = isValid([...ProductsValidationKeys]);
        return Object.keys(messages).length === 0;
    };
    const adPreferenceValidator = () => {
        const messages = isValid([
            ...AdPlacementValidationKeys,
            ...AdPreferenceValidationKeys,
        ]);
        return Object.keys(messages).length === 0;
    };
    const reviewCampaignAction = () => __awaiter(void 0, void 0, void 0, function* () {
        if (isAnyNudgeVisible) {
            setShowWarningModal(true);
            return false;
        }
        return yield createCampaignOperation();
    });
    const createCampaignOperation = () => __awaiter(void 0, void 0, void 0, function* () {
        let success = false;
        try {
            const accountId = yield getCurrentAccountId();
            const result = yield createCampaignApi({
                campaign,
                accountId,
            });
            success = !!result;
            return !!result;
        }
        catch (_b) {
            success = false;
            return false;
        }
        finally {
            success && setShowFinalStep(true);
        }
    });
    const steps = [
        {
            title: 'Placement',
            component: React.createElement(AdPlacement, { mode: CampaignWriteModeEnum.CREATE }),
            validator: adPlacementValidator,
        },
        {
            title: 'Ad Preference',
            component: React.createElement(AdPreference, { mode: CampaignWriteModeEnum.CREATE }),
            validator: adPreferenceValidator,
        },
        {
            title: 'Search Products',
            component: React.createElement(Products, { mode: CampaignWriteModeEnum.CREATE }),
            validator: productsValidator,
        },
        {
            title: 'Choose Keywords',
            component: React.createElement(Keywords, { mode: CampaignWriteModeEnum.CREATE }),
            validator: keywordsValidator,
        },
        {
            title: 'Budget',
            component: React.createElement(Budget, { mode: CampaignWriteModeEnum.CREATE }),
            validator: budgetValidator,
        },
        {
            title: 'Campaign Preview',
            hideInNav: true,
            component: (React.createElement(ReviewCampaign, { mode: CampaignWriteModeEnum.CREATE, setStepperActiveStep: (_a = stepperRef.current) === null || _a === void 0 ? void 0 : _a.setActiveStep, showWarningModal: showWarningModal, campaignOperation: createCampaignOperation, setShowWarningModal: setShowWarningModal })),
            action: reviewCampaignAction,
        },
    ];
    const handleGotoDashboard = () => {
        navigate(Routes.INSTAMART_HOME, {
            replace: true,
            preventScrollReset: true,
        });
    };
    const renderError = () => {
        return (React.createElement("div", { className: styles.containerCenter },
            React.createElement(Text, { variant: DlsFontVariants.subheader_S1_Bold, color: DlsColorTokens.text_Med_Emphasis }, "Something went wrong!")));
    };
    const renderContent = () => (React.createElement("div", null, showFinalStep ? (React.createElement("div", { className: styles.successContainer },
        React.createElement("img", { src: getCloudinaryUrl(IMAGE.src, {
                width: IMAGE.width,
            }), className: styles.successImage }),
        React.createElement(Button, { variant: "primary", onClick: handleGotoDashboard, className: styles.successButton },
            "Go to Dashboard",
            React.createElement(ArrowRight, { className: styles.successButtonIcon, fill: "#fff" })))) : (React.createElement(Stepper, { steps: steps, ref: stepperRef }))));
    if (isError) {
        return renderError();
    }
    if (setupInProgress) {
        return null;
    }
    return renderContent();
};
export default Index;
